import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { StatusPill } from '@/components/primitives';
import type { PublicLocationDetailCharger } from '@/lib/api';
import {
  canStart, inMaintenance, isMultiPlug, notOpenToGuests, plugSpec, stallDetail, stallModel, stallName,
  stallStartPath, stallStatus, type StartOrigin, type StallStatusView,
} from '@/lib/stallLabels';
import styles from './StallList.module.css';

/**
 * The stall list with a Start button on every stall (Warren, 2026-10-23:
 * "trim the fat"). One list for both surfaces that show a location: the
 * map's location sheet and the station page (deep links, QR stickers
 * without a charger, Favorites). Before this the sheet listed the chargers
 * read-only and the station page listed them again behind "Choose a
 * stall", with a radio and a pinned Start; now a tap on Start opens the
 * pre-session route directly, and that page keeps every start gate.
 *
 * Each stall leads with its name ("Visitor Spot", the identity when the
 * operator never named it); model, plug type and power sit underneath,
 * small. A single-plug charger is one row; a multi-plug charger is a
 * header with one row per plug, because the charger only starts the port
 * it is told to.
 */
interface Props {
  locationId: string;
  chargers: PublicLocationDetailCharger[];
  onNavigate: (path: string) => void;
  /** Shown when there are no stalls. */
  emptyText: string;
  /** Item 6: "This charger isn't open to guests yet" on each flagged
   *  stall. Off where a header already says it for every stall. */
  showGuestNotes?: boolean;
  /** Where the Start buttons are, carried to the pre-session route so its
   *  Back returns here. The map's sheet passes 'map'; the station page
   *  leaves it out and Back keeps going to the station page. */
  startFrom?: StartOrigin;
}

export function StallList({
  locationId, chargers, onNavigate, emptyText, showGuestNotes = true, startFrom,
}: Props) {
  if (chargers.length === 0) return <p className={styles.empty}>{emptyText}</p>;
  // One clock for the whole list so every row agrees on the maintenance
  // check within a render.
  const now = new Date();
  return (
    <ul className={styles.list}>
      {chargers.map((c) => {
        // A guest-closed charger (public mode shows it, the operator has
        // not opened it) says so too, and its Start is off below.
        const guestNote = showGuestNotes && notOpenToGuests(c);
        const maintenance = inMaintenance(c, now);
        return (
          <li key={c._id} className={styles.item}>
            {isMultiPlug(c) ? (
              <MultiPlugStall
                charger={c}
                maintenance={maintenance}
                guestNote={guestNote}
                onStart={(connectorId) => onNavigate(stallStartPath(locationId, c, connectorId, startFrom))}
              />
            ) : (
              <SinglePlugStall
                charger={c}
                maintenance={maintenance}
                guestNote={guestNote}
                // The only plug there is. 1 is the OCPP default the
                // driver-start route assumes when a doc carries no
                // connectors[] yet, so the two agree on legacy data.
                onStart={() => onNavigate(stallStartPath(locationId, c, c.connectors?.[0]?.connector_id ?? 1, startFrom))}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Localized pill copy. The mapping's own `label` is the English fallback
 *  so an unmapped key never renders blank. */
function statusLabel(t: TFunction, view: StallStatusView): string {
  return t(`connectorStatus.${view.key}`, { defaultValue: view.label });
}

function SinglePlugStall({
  charger, maintenance, guestNote, onStart,
}: {
  charger: PublicLocationDetailCharger;
  maintenance: boolean;
  guestNote: boolean;
  onStart: () => void;
}) {
  const { t } = useTranslation('station');
  const name = stallName(charger);
  const detail = stallDetail(charger, (kw) => t('plug.kw', { kw }));
  const view = stallStatus(charger.connectors?.[0]?.status, charger.is_online, maintenance);
  return (
    <div className={styles.row}>
      <div className={styles.info}>
        <span className={styles.name}>{name}</span>
        {detail && <span className={styles.detail}>{detail}</span>}
        {guestNote && <span className={styles.guestNote}>{t('guestNotOpenYet')}</span>}
        <span className={styles.pill}>
          <StatusPill tone={view.tone} variant="soft" size="sm">{statusLabel(t, view)}</StatusPill>
        </span>
      </div>
      <StartButton
        disabled={!canStart(charger, view)}
        onClick={onStart}
        ariaLabel={t('cta.startCharging', { identity: name })}
      />
    </div>
  );
}

function MultiPlugStall({
  charger, maintenance, guestNote, onStart,
}: {
  charger: PublicLocationDetailCharger;
  maintenance: boolean;
  guestNote: boolean;
  onStart: (connectorId: number) => void;
}) {
  const { t } = useTranslation('station');
  const name = stallName(charger);
  const connectors = charger.connectors ?? [];
  const detail = [stallModel(charger), t('plug.count', { n: connectors.length })].filter(Boolean).join(' · ');
  return (
    <div className={styles.group}>
      <div className={styles.info}>
        <span className={styles.name}>{name}</span>
        <span className={styles.detail}>{detail}</span>
        {guestNote && <span className={styles.guestNote}>{t('guestNotOpenYet')}</span>}
      </div>
      <div className={styles.plugList} role="group" aria-label={t('plug.listAria', { identity: name })}>
        {connectors.map((conn) => {
          // A charger that is offline or in maintenance takes every plug
          // with it; otherwise each plug carries its own live status.
          const view = stallStatus(conn.status, charger.is_online, maintenance);
          const spec = plugSpec(conn, (kw) => t('plug.kw', { kw }));
          return (
            <div key={conn.connector_id} className={styles.plugRow}>
              <div className={styles.info}>
                <span className={styles.plugName}>{t('plug.label', { n: conn.connector_id })}</span>
                {spec && <span className={styles.detail}>{spec}</span>}
                <span className={styles.pill}>
                  <StatusPill tone={view.tone} variant="soft" size="sm">{statusLabel(t, view)}</StatusPill>
                </span>
              </div>
              <StartButton
                disabled={!canStart(charger, view)}
                onClick={() => onStart(conn.connector_id)}
                // The charger's name too, so two chargers' "Plug 1"
                // buttons never share an accessible name.
                ariaLabel={t('cta.startChargingPlug', { identity: name, plug: conn.connector_id })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The visible word is "Start"; the accessible name says which stall or
 *  plug ("Start Charging · Visitor Spot", "Start Charging · Visitor Spot ·
 *  Plug 2") and begins with that word. */
function StartButton({
  disabled, onClick, ariaLabel,
}: { disabled: boolean; onClick: () => void; ariaLabel: string }) {
  const { t } = useTranslation('station');
  return (
    <button
      type="button"
      className={styles.start}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {t('stall.start')}
    </button>
  );
}
